import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SharingBtn } from "@/components/Sharing";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Check, ListChecks, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { bulkProcessDistributions } from "@/Api/distributions";
import {
  acceptDistribution,
  rejectDistribution,
  acceptDistributionSellerToUnity,
  rejectDistributionSellerToUnity,
  acceptDistributionBetweenUnities,
  rejectDistributionBetweenUnities,
} from "@/Api/distributionsProcess";
import { useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DISTRIBUTION_PROCESS_STATUS,
  DISTRIBUTION_TYPE,
} from "@/constants/distributionStatus";
import { Mask } from "@/utils/mask";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

type ProductType = {
  id: number;
  name: string;
  classification: string;
  quotas: number;
  lottery_id?: number;
  quota_quantity_utilized: number;
  distribution_process_uuid?: string;
  distribution_process_status_id: number;
  distribution_process_status_name: string;
  distribution_type_id?: number;
  updated_at?: string;
  updated_by?: {
    name: string;
  };
  created_by?: {
    name: string;
  };
  created_at?: string;
  from_unity_name?: string;
  from_seller_name?: string;
  origin_type?: "unity" | "seller";
  product_classification?: {
    name: string;
    quota_quantity: number;
    game_quantity: number;
    dez_quantity: number;
    sale_price: number;
  };
};

type ContestType = {
  id: string;
  name: string | null;
  lottery: string;
  lottery_id?: number;
  lottery_color?: string;
  products: ProductType[];
};

type GroupedDistributionsType = {
  contests: ContestType[];
};

type PaginationType = {
  currentPage: number;
  lastPage: number;
  loadingMore: boolean;
  handleLoadMore: () => void;
};

export const TableData = ({
  groupedData,
  pageActions,
  pagination,
}: {
  groupedData: GroupedDistributionsType;
  pageActions: {
    update: () => void;
  };
  pagination?: PaginationType;
}) => {
  const [loadingActions, setLoadingActions] = useState<{
    [key: string]: boolean;
  }>({});
  const [showAcceptAllDialog, setShowAcceptAllDialog] = useState(false);
  const [isAcceptingAll, setIsAcceptingAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedUuids, setSelectedUuids] = useState<Set<string>>(new Set());
  const [showAcceptSelectedDialog, setShowAcceptSelectedDialog] =
    useState(false);
  const [isAcceptingSelected, setIsAcceptingSelected] = useState(false);

  const pendingOf = (products: ProductType[]) =>
    products.filter(
      (product) =>
        !!product.distribution_process_uuid &&
        product.distribution_process_status_id ===
          DISTRIBUTION_PROCESS_STATUS.PENDING
    );

  const toggleSelectionMode = () => {
    setSelectionMode((prev) => !prev);
    setSelectedUuids(new Set());
  };

  const toggleSelected = (uuid: string) => {
    setSelectedUuids((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  };

  const toggleAllSelected = (products: ProductType[]) => {
    const uuids = pendingOf(products).map(
      (product) => product.distribution_process_uuid!
    );
    setSelectedUuids((prev) => {
      const allSelected = uuids.every((uuid) => prev.has(uuid));
      const next = new Set(prev);
      uuids.forEach((uuid) =>
        allSelected ? next.delete(uuid) : next.add(uuid)
      );
      return next;
    });
  };

  const handleAcceptSelected = async () => {
    const uuids = Array.from(selectedUuids);
    if (uuids.length === 0) return;

    setIsAcceptingSelected(true);
    try {
      const response = await bulkProcessDistributions({
        action: "accept",
        uuids,
      });
      const payload = response?.data?.response as
        | { processed: number; failed: number; results: { error: string | null; success: boolean }[] }
        | undefined;

      // Erros de rede/HTTP já são notificados pelo interceptor do axios.
      if (!response?.ok || !payload) return;

      if (payload.failed === 0) {
        toast.success(
          `${payload.processed} distribuição(ões) aceita(s) com sucesso!`
        );
      } else if (payload.processed > 0) {
        toast.warning(
          `${payload.processed} aceita(s), ${payload.failed} falharam na aceitação.`
        );
      } else {
        toast.error(
          payload.results.find((result) => !result.success)?.error ||
            "Nenhuma distribuição pôde ser aceita."
        );
      }

      setSelectedUuids(new Set());
      setSelectionMode(false);
      pageActions.update();
    } finally {
      setIsAcceptingSelected(false);
      setShowAcceptSelectedDialog(false);
    }
  };

  const handleAcceptDistribution = async (
    uuid: string,
    productName: string,
    distributionTypeId?: number
  ) => {
    setLoadingActions((prev) => ({ ...prev, [`accept-${uuid}`]: true }));
    try {
      let data;
      // Tipos que a unidade aceita:
      // 1: BY_UNITY (Admin -> Unidade) - usa acceptDistribution
      // 3: BETWEEN_UNITY (Entre Unidades) - usa acceptDistributionBetweenUnities
      // 5: OF_SELLER_FOR_UNITY (Vendedor -> Unidade) - usa acceptDistributionSellerToUnity
      if (distributionTypeId === DISTRIBUTION_TYPE.OF_SELLER_FOR_UNITY) {
        data = await acceptDistributionSellerToUnity(uuid);
      } else if (distributionTypeId === DISTRIBUTION_TYPE.BETWEEN_UNITY) {
        data = await acceptDistributionBetweenUnities(uuid);
      } else {
        data = await acceptDistribution(uuid);
      }
      if (data.ok) {
        toast.success(`Distribuição aceita, do produto: ${productName} `);
        pageActions.update();
      }
    } finally {
      setLoadingActions((prev) => ({ ...prev, [`accept-${uuid}`]: false }));
    }
  };

  const handleRejectDistribution = async (
    uuid: string,
    productName: string,
    distributionTypeId?: number
  ) => {
    setLoadingActions((prev) => ({ ...prev, [`reject-${uuid}`]: true }));
    try {
      // Tipos que a unidade rejeita:
      // 1: BY_UNITY (Admin -> Unidade) - usa rejectDistribution
      // 3: BETWEEN_UNITY (Entre Unidades) - usa rejectDistributionBetweenUnities
      // 5: OF_SELLER_FOR_UNITY (Vendedor -> Unidade) - usa rejectDistributionSellerToUnity
      if (distributionTypeId === DISTRIBUTION_TYPE.OF_SELLER_FOR_UNITY) {
        await rejectDistributionSellerToUnity(uuid);
      } else if (distributionTypeId === DISTRIBUTION_TYPE.BETWEEN_UNITY) {
        await rejectDistributionBetweenUnities(uuid);
      } else {
        await rejectDistribution(uuid);
      }
      toast.success(`Distribuição rejeitada, do produto: ${productName} `);

      pageActions.update();
    } finally {
      setLoadingActions((prev) => ({ ...prev, [`reject-${uuid}`]: false }));
    }
  };

  const handleAcceptAll = async () => {
    const allPendingDistributions = groupedData.contests.flatMap((contest) =>
      contest.products.filter(
        (product) =>
          product.distribution_process_status_id ===
          DISTRIBUTION_PROCESS_STATUS.PENDING
      )
    );

    if (allPendingDistributions.length === 0) {
      toast.error("Nenhuma distribuição pendente encontrada.");
      return;
    }

    setIsAcceptingAll(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const product of allPendingDistributions) {
        if (!product.distribution_process_uuid) continue;

        try {
          let result;
          // Aceita apenas tipos válidos para unidade: 1, 3, 5
          if (
            product.distribution_type_id ===
            DISTRIBUTION_TYPE.OF_SELLER_FOR_UNITY
          ) {
            result = await acceptDistributionSellerToUnity(
              product.distribution_process_uuid
            );
          } else if (
            product.distribution_type_id === DISTRIBUTION_TYPE.BETWEEN_UNITY
          ) {
            result = await acceptDistributionBetweenUnities(
              product.distribution_process_uuid
            );
          } else {
            result = await acceptDistribution(
              product.distribution_process_uuid
            );
          }

          if (result.ok) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          console.error(
            `Erro ao aceitar distribuição ${product.distribution_process_uuid}:`,
            error
          );
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(
          `${successCount} distribuição(ões) aceita(s) com sucesso!`
        );
        pageActions.update();
      }

      if (errorCount > 0) {
        toast.error(`${errorCount} distribuição(ões) falharam na aceitação.`);
      }
    } finally {
      setIsAcceptingAll(false);
      setShowAcceptAllDialog(false);
    }
  };
  return (
    <div className="flex flex-col gap-4">
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Concurso</TableHead>
            <TableHead>Loteria</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groupedData.contests.map((contest) => (
            <TableRow key={contest.id}>
              <TableCell>{contest.name || "Não informado"}</TableCell>
              <TableCell>
                <span
                  className="px-2 py-1 rounded text-white text-xs font-medium"
                  style={{ backgroundColor: contest.lottery_color || "#666" }}
                >
                  {contest.lottery}
                </span>
              </TableCell>
              <TableCell className="text-center">
                <div className="flex justify-center gap-1">
                  <SharingBtn
                    labels={{
                      title: "Distribuir Cotas",
                      description: "Distribua as cotas para as unidade",
                    }}
                    params={{
                      func: async () => ({
                        data: { response: contest },
                        ok: true,
                        status: 200,
                        statusText: "OK",
                        headers: {},
                        config: {} as any,
                      }),
                      funcParams: contest.id,
                    }}
                    className="sm:max-w-[700px]"
                    render={() => {
                      const filteredProducts = contest.products.filter((product) => {
                        if (!searchTerm.trim()) return true;
                        const search = searchTerm.toLowerCase();
                        const productName = `${product.name} ${product.classification}`.toLowerCase();
                        return productName.includes(search);
                      });

                      return (
                      <div className="flex flex-col h-[70vh] w-full min-w-0">
                        {/* Header fixo */}
                        <div className="sticky top-0 bg-white dark:bg-gray-900 z-10 pb-4 space-y-4">
                          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center sm:gap-4">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                              <h3 className="font-semibold text-lg">
                                Produtos disponíveis:
                              </h3>
                              {/* Campo de busca */}
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-700 dark:text-gray-300">B:</span>
                                <Input
                                  placeholder="Ex: 200"
                                  value={searchTerm}
                                  onChange={(e) => setSearchTerm(e.target.value)}
                                  className="w-full min-w-0 sm:w-32"
                                />
                                <Button
                                  variant="default"
                                  className="bg-gray-800 hover:bg-gray-900 text-white"
                                  onClick={() => {}}
                                >
                                  <Search className="h-4 w-4 mr-2" />
                                  Buscar
                                </Button>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <Button
                                variant="outline"
                                onClick={toggleSelectionMode}
                                disabled={isAcceptingAll || isAcceptingSelected}
                                className={
                                  selectionMode
                                    ? "bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200"
                                    : "bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                                }
                              >
                                <ListChecks className="h-4 w-4 mr-2" />
                                {selectionMode ? "Cancelar seleção" : "Selecionar"}
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => setShowAcceptAllDialog(true)}
                                disabled={isAcceptingAll || isAcceptingSelected}
                                className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                              >
                                {isAcceptingAll ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                                    Aceitando...
                                  </>
                                ) : (
                                  "Aceitar Tudo"
                                )}
                              </Button>
                            </div>
                          </div>
                          {selectionMode && (
                            <div className="flex flex-col gap-2 p-2 rounded-md border bg-blue-50 dark:bg-gray-800 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                              <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <Checkbox
                                  checked={
                                    pendingOf(filteredProducts).length > 0 &&
                                    pendingOf(filteredProducts).every((product) =>
                                      selectedUuids.has(
                                        product.distribution_process_uuid!
                                      )
                                    )
                                  }
                                  onCheckedChange={() =>
                                    toggleAllSelected(filteredProducts)
                                  }
                                  disabled={
                                    pendingOf(filteredProducts).length === 0
                                  }
                                />
                                Selecionar todos os pendentes
                              </label>
                              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                                <span className="text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                  {selectedUuids.size} selecionado(s)
                                </span>
                                <Button
                                  size="sm"
                                  onClick={() => setShowAcceptSelectedDialog(true)}
                                  disabled={
                                    selectedUuids.size === 0 || isAcceptingSelected
                                  }
                                  className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white"
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  Aceitar selecionados ({selectedUuids.size})
                                </Button>
                              </div>
                            </div>
                          )}
                          <div
                            className="flex items-center justify-center px-3 py-1.5 rounded-md"
                            style={{
                              background:
                                contest?.lottery_color || "transparent",
                            }}
                          >
                            <h3 className="font-semibold text-base text-white text-center">
                              {contest.lottery} - Concurso {contest.name}
                            </h3>
                          </div>
                        </div>

                        {/* Lista de produtos com scroll */}
                        <ScrollArea className="flex-1 [&>[data-radix-scroll-area-viewport]>div]:!block">
                          <div className="flex flex-col gap-2 pr-4">
                            {filteredProducts.length === 0 ? (
                              <div className="text-center py-8 text-gray-500">
                                Nenhum bolão encontrado com o termo "{searchTerm}"
                              </div>
                            ) : (
                            filteredProducts.map((product) => (
                              <div
                                key={product.id}
                                className="border border-l-4 p-3 rounded"
                                style={{
                                  borderLeftColor:
                                    contest.lottery_color || "#666",
                                }}
                              >
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                  {selectionMode &&
                                    product.distribution_process_uuid && (
                                      <Checkbox
                                        className="self-start sm:self-center sm:mr-4"
                                        aria-label={`Selecionar ${product.name} - ${product.classification}`}
                                        checked={selectedUuids.has(
                                          product.distribution_process_uuid
                                        )}
                                        onCheckedChange={() =>
                                          toggleSelected(
                                            product.distribution_process_uuid!
                                          )
                                        }
                                        disabled={
                                          product.distribution_process_status_id !==
                                          DISTRIBUTION_PROCESS_STATUS.PENDING
                                        }
                                      />
                                    )}
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium break-words">
                                      {product.name} - {product.classification}
                                    </p>
                                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                                      <span className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 px-2.5 py-0.5 rounded">
                                        {product?.quotas} cotas para receber
                                      </span>
                                      <span
                                        className={`px-2.5 py-0.5 rounded text-sm font-medium ${
                                          product.distribution_process_status_id ===
                                          DISTRIBUTION_PROCESS_STATUS.PENDING
                                            ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"
                                            : product.distribution_process_status_id ===
                                              DISTRIBUTION_PROCESS_STATUS.ACCEPTED
                                            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                                            : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                                        }`}
                                      >
                                        {
                                          product.distribution_process_status_name
                                        }
                                      </span>
                                    </div>
                                    {(product.distribution_process_status_id ==
                                      DISTRIBUTION_PROCESS_STATUS.ACCEPTED ||
                                      product.distribution_process_status_id ==
                                        DISTRIBUTION_PROCESS_STATUS.REJECTED ||
                                      product.distribution_process_status_id ==
                                        DISTRIBUTION_PROCESS_STATUS.PENDING) &&
                                      (product.updated_at ||
                                        product.created_by ||
                                        product.updated_by) && (
                                        <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg border">
                                          <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">📅 Informações</span>
                                            {(product.from_unity_name || product.from_seller_name) && (
                                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                                {product.origin_type === "seller" ? (
                                                  <span className="inline-flex items-center gap-1">
                                                    <span className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 px-1.5 py-0.5 rounded text-xs">Vendedor</span>
                                                    {product.from_seller_name}
                                                  </span>
                                                ) : (
                                                  <span className="inline-flex items-center gap-1">
                                                    <span className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 px-1.5 py-0.5 rounded text-xs">Unidade</span>
                                                    {product.from_unity_name}
                                                  </span>
                                                )}
                                              </span>
                                            )}
                                          </div>
                                          <div className="grid grid-cols-1 min-[420px]:grid-cols-2 md:grid-cols-4 gap-3 text-sm break-words">
                                            {product.created_by?.name && (
                                              <div className="text-gray-600 dark:text-gray-400">
                                                <span className="font-medium block text-xs text-gray-500">Criado por</span>
                                                {product.created_by.name}
                                              </div>
                                            )}
                                            {product.created_at && (
                                              <div className="text-gray-600 dark:text-gray-400">
                                                <span className="font-medium block text-xs text-gray-500">Data de criação</span>
                                                {Mask.dateTime(product.created_at)}
                                              </div>
                                            )}
                                            {product.updated_by?.name && (
                                              <div className="text-gray-600 dark:text-gray-400">
                                                <span className="font-medium block text-xs text-gray-500">
                                                  {product.distribution_process_status_id === DISTRIBUTION_PROCESS_STATUS.ACCEPTED ? "Aceito por" : "Rejeitado por"}
                                                </span>
                                                {product.updated_by.name}
                                              </div>
                                            )}
                                            {product.updated_at && (
                                              <div className="text-gray-600 dark:text-gray-400">
                                                <span className="font-medium block text-xs text-gray-500">Data</span>
                                                {Mask.dateTime(product.updated_at)}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                  {product.distribution_process_uuid &&
                                    product.distribution_process_status_id ===
                                      DISTRIBUTION_PROCESS_STATUS.PENDING && (
                                      <div className="flex flex-wrap gap-2">
                                        <AlertDialog>
                                          <AlertDialogTrigger asChild>
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="text-green-600 border-green-600 hover:bg-green-50"
                                              disabled={
                                                loadingActions[
                                                  `accept-${product.distribution_process_uuid}`
                                                ]
                                              }
                                            >
                                              <Check className="w-4 h-4 mr-1" />
                                              Aceitar
                                            </Button>
                                          </AlertDialogTrigger>
                                          <AlertDialogContent>
                                            <AlertDialogHeader>
                                              <AlertDialogTitle>
                                                Confirmar Aceitação
                                              </AlertDialogTitle>
                                              <AlertDialogDescription>
                                                Tem certeza que deseja aceitar a
                                                distribuição de{" "}
                                                <strong>
                                                  {product.quotas} cotas
                                                </strong>{" "}
                                                do produto{" "}
                                                <strong>
                                                  {product.name} -{" "}
                                                  {product.classification}
                                                </strong>
                                                ?
                                              </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                              <AlertDialogCancel>
                                                Cancelar
                                              </AlertDialogCancel>
                                              <AlertDialogAction
                                                onClick={() =>
                                                  handleAcceptDistribution(
                                                    product.distribution_process_uuid!,
                                                    `${product.name} - ${product.classification}`,
                                                    product.distribution_type_id
                                                  )
                                                }
                                                className="bg-green-600 hover:bg-green-700"
                                              >
                                                Confirmar Aceitação
                                              </AlertDialogAction>
                                            </AlertDialogFooter>
                                          </AlertDialogContent>
                                        </AlertDialog>

                                        <AlertDialog>
                                          <AlertDialogTrigger asChild>
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="text-red-600 border-red-600 hover:bg-red-50"
                                              disabled={
                                                loadingActions[
                                                  `reject-${product.distribution_process_uuid}`
                                                ]
                                              }
                                            >
                                              <X className="w-4 h-4 mr-1" />
                                              Rejeitar
                                            </Button>
                                          </AlertDialogTrigger>
                                          <AlertDialogContent>
                                            <AlertDialogHeader>
                                              <AlertDialogTitle>
                                                Confirmar Rejeição
                                              </AlertDialogTitle>
                                              <AlertDialogDescription>
                                                Tem certeza que deseja rejeitar
                                                a distribuição de{" "}
                                                <strong>
                                                  {product.quotas} cotas
                                                </strong>{" "}
                                                do produto{" "}
                                                <strong>
                                                  {product.name} -{" "}
                                                  {product.classification}
                                                </strong>
                                                ?
                                              </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                              <AlertDialogCancel>
                                                Cancelar
                                              </AlertDialogCancel>
                                              <AlertDialogAction
                                                onClick={() =>
                                                  handleRejectDistribution(
                                                    product.distribution_process_uuid!,
                                                    `${product.name} - ${product.classification}`,
                                                    product.distribution_type_id
                                                  )
                                                }
                                                className="bg-red-600 hover:bg-red-700"
                                              >
                                                Confirmar Rejeição
                                              </AlertDialogAction>
                                            </AlertDialogFooter>
                                          </AlertDialogContent>
                                        </AlertDialog>
                                      </div>
                                    )}
                                </div>
                              </div>
                            ))
                            )}
                            {pagination && pagination.currentPage < pagination.lastPage && (
                              <div className="flex justify-center mt-4">
                                <Button
                                  variant="outline"
                                  onClick={pagination.handleLoadMore}
                                  disabled={pagination.loadingMore}
                                >
                                  {pagination.loadingMore ? "Carregando..." : "Ver mais"}
                                </Button>
                              </div>
                            )}
                          </div>
                        </ScrollArea>
                      </div>
                    );
                    }}
                  />
                </div>
              </TableCell>
            </TableRow>
          ))}
          {groupedData.contests.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className="text-center">
                Nada foi encontrado.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog
        open={showAcceptSelectedDialog}
        onOpenChange={setShowAcceptSelectedDialog}
      >
        <DialogContent className="z-[70]" style={{ zIndex: 70 }}>
          <DialogHeader>
            <DialogTitle>Aceitar Distribuições Selecionadas</DialogTitle>
            <DialogDescription>
              Você está prestes a aceitar {selectedUuids.size} distribuição(ões).
              Somente as selecionadas serão aceitas. Distribuições que falharem
              serão reportadas.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-64 overflow-y-auto p-3 bg-gray-50 dark:bg-gray-800 rounded">
            {groupedData.contests
              .flatMap((contest) => contest.products)
              .filter(
                (product) =>
                  product.distribution_process_uuid &&
                  selectedUuids.has(product.distribution_process_uuid)
              )
              .map((product) => (
                <p
                  key={product.id}
                  className="text-sm text-gray-600 dark:text-gray-300"
                >
                  • {product.name} - {product.classification} ({product.quotas}{" "}
                  cotas)
                </p>
              ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAcceptSelectedDialog(false)}
              disabled={isAcceptingSelected}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAcceptSelected}
              disabled={isAcceptingSelected || selectedUuids.size === 0}
              className="bg-green-600 hover:bg-green-700"
            >
              {isAcceptingSelected ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                  Aceitando...
                </>
              ) : (
                `Confirmar Aceitação (${selectedUuids.size})`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAcceptAllDialog} onOpenChange={setShowAcceptAllDialog}>
        <DialogContent className="z-[70]" style={{ zIndex: 70 }}>
          <DialogHeader>
            <DialogTitle>Aceitar Todas as Distribuições Pendentes</DialogTitle>
            <DialogDescription>
              {(() => {
                const allPendingDistributions = groupedData.contests.flatMap(
                  (contest) =>
                    contest.products.filter(
                      (product) =>
                        product.distribution_process_status_id ===
                        DISTRIBUTION_PROCESS_STATUS.PENDING
                    )
                );

                const distributionsByType = {
                  unityToUnity: allPendingDistributions.filter(
                    (p) =>
                      p.distribution_type_id === DISTRIBUTION_TYPE.BETWEEN_UNITY
                  ),
                  sellerToUnity: allPendingDistributions.filter(
                    (p) =>
                      p.distribution_type_id ===
                      DISTRIBUTION_TYPE.OF_SELLER_FOR_UNITY
                  ),
                  other: allPendingDistributions.filter(
                    (p) =>
                      !p.distribution_type_id ||
                      (p.distribution_type_id !==
                        DISTRIBUTION_TYPE.BETWEEN_UNITY &&
                        p.distribution_type_id !==
                          DISTRIBUTION_TYPE.OF_SELLER_FOR_UNITY)
                  ),
                };

                return (
                  <div className="space-y-3">
                    <p>
                      Você está prestes a aceitar todas as distribuições
                      pendentes.
                    </p>

                    {allPendingDistributions.length > 0 ? (
                      <>
                        <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded max-h-96 overflow-y-auto">
                          <p className="text-base font-medium mb-2">
                            Resumo das distribuições (
                            {allPendingDistributions.length} no total):
                          </p>

                          {distributionsByType.unityToUnity.length > 0 && (
                            <div className="mb-2">
                              <p className="text-sm font-medium text-blue-600 dark:text-blue-400">
                                Entre Unidades (
                                {distributionsByType.unityToUnity.length}):
                              </p>
                              {distributionsByType.unityToUnity.map((product) => (
                                  <p
                                    key={product.id}
                                    className="text-sm text-gray-600 dark:text-gray-300 ml-2"
                                  >
                                    • {product.name} - {product.classification}{" "}
                                    ({product.quota_quantity_utilized} cotas)
                                  </p>
                                ))}
                            </div>
                          )}

                          {distributionsByType.sellerToUnity.length > 0 && (
                            <div className="mb-2">
                              <p className="text-sm font-medium text-green-600 dark:text-green-400">
                                De Vendedor para Unidade (
                                {distributionsByType.sellerToUnity.length}):
                              </p>
                              {distributionsByType.sellerToUnity.map((product) => (
                                  <p
                                    key={product.id}
                                    className="text-sm text-gray-600 dark:text-gray-300 ml-2"
                                  >
                                    • {product.name} - {product.classification}{" "}

                                  </p>
                                ))}
                            </div>
                          )}

                          {distributionsByType.other.length > 0 && (
                            <div className="mb-2">
                              <p className="text-sm font-medium text-purple-600 dark:text-purple-400">
                                Outras Distribuições (
                                {distributionsByType.other.length}):
                              </p>
                              {distributionsByType.other.map((product) => (
                                  <p
                                    key={product.id}
                                    className="text-sm text-gray-600 dark:text-gray-300 ml-2"
                                  >
                                    • {product.name} - {product.classification}{" "}
                                    ({product.quota_quantity_utilized} cotas)
                                  </p>
                                ))}
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Esta ação processará cada distribuição individualmente
                          usando o endpoint apropriado para cada tipo.
                          Distribuições que falharem serão reportadas
                          separadamente.
                        </p>
                      </>
                    ) : (
                      <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900 rounded">
                        <p className="text-sm text-yellow-800 dark:text-yellow-200">
                          ⚠️ Nenhuma distribuição pendente encontrada.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAcceptAllDialog(false)}
              disabled={isAcceptingAll}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAcceptAll}
              disabled={isAcceptingAll}
              className="bg-green-600 hover:bg-green-700"
            >
              {isAcceptingAll ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                  Aceitando...
                </>
              ) : (
                "Confirmar Aceitação em Lote"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
